import { computed, inject, ref } from 'vue';
import type { Ref } from 'vue';
import { teamsClient } from '~/services';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import type { Features } from '~/provides/features.types';
import { catchRequestError } from '~/utils/request';

import { createGlobalState } from '@vueuse/core';

export const useCostVisibility = createGlobalState(() => {
    const FEATURES = inject('FEATURES') as Features;
    const settings: Ref<{
        org_units: boolean;
        groups: boolean;
    } | null> = ref(null);
    const loading = ref(false);

    const { onCorporate, currentGroup, currentOrgUnit } = useCurrentGroup();

    const showEstimatedCost = computed((): boolean => {
        if (!FEATURES.new_billing_implementation) {
            return false;
        }

        // Do not display cost information to BSS or CBW sub-teams
        if (Spark.state.currentTeam.parent_id === 101068 || Spark.state.currentTeam.parent_id === 101991) {
            return false;
        }

        if (onCorporate()) {
            return true;
        }

        if (null === settings.value) {
            return false;
        }

        return Boolean(
            (currentGroup.value?.group_id && settings.value.groups) ||
                (currentOrgUnit.value?.id && settings.value.org_units)
        );
    });

    const mount = async (): Promise<void> => {
        if (settings.value || loading.value) {
            return;
        }

        if (onCorporate()) {
            return;
        }

        if (!FEATURES.new_billing_implementation) {
            return;
        }

        loading.value = true;

        try {
            const teamId = Spark.state.currentTeam.id;
            const response = await teamsClient.getCostVisibility(teamId);
            settings.value = response.data;
        } catch (e) {
            console.error(catchRequestError(e));
        } finally {
            loading.value = false;
        }
    };

    const unmount = (): void => {
        settings.value = null;
        loading.value = false;
    };

    mount();

    return {
        showEstimatedCost,
        mount,
        unmount,
    };
});
